import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminInventoryApi from "../services/adminInventoryApi";
import "../styles/Admin.css";

function AdminReceiveManufacturedPage({ adminEmail, onLogout }) {
  const navigate = useNavigate();

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");

  const [batchQuery, setBatchQuery] = useState("");
  const [batches, setBatches] = useState([]);
  const [batchesTotal, setBatchesTotal] = useState(0);
  const [selectedBatchId, setSelectedBatchId] = useState("");

  const [note, setNote] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedBranch = useMemo(
    () => branches.find((b) => String(b.id) === String(branchId)) || null,
    [branches, branchId]
  );

  const selectedBatch = useMemo(
    () => batches.find((b) => String(b.id) === String(selectedBatchId)) || null,
    [batches, selectedBatchId]
  );

  async function loadBranches() {
    const data = await adminInventoryApi.listBranches();
    if (!data || data.ok !== true) throw new Error("branches_failed");
    const bs = data.branches || [];
    setBranches(bs);
    if (!branchId && bs[0]) setBranchId(String(bs[0].id));
    return bs;
  }

  async function loadLocations(bId) {
    if (!bId) {
      setLocations([]);
      setLocationId("");
      return;
    }
    const data = await adminInventoryApi.listLocations(bId);
    if (!data || data.ok !== true) throw new Error("locations_failed");
    const ls = data.locations || [];
    setLocations(ls);
    if (!locationId && ls[0]) setLocationId(String(ls[0].id));
  }

  useEffect(() => {
    (async () => {
      try {
        const bs = await loadBranches();
        const effectiveBranchId = branchId || (bs[0] ? String(bs[0].id) : "");
        if (effectiveBranchId) await loadLocations(effectiveBranchId);

        // initial load of receivable batches
        const bres = await adminInventoryApi.listReceivableBatches({ limit: 50, offset: 0 });
        if (bres?.ok) {
          setBatches(Array.isArray(bres.rows) ? bres.rows : []);
          setBatchesTotal(Number(bres.total || 0));
        }
      } catch (e) {
        console.error("[AdminReceiveManufacturedPage] init error:", e);
        setError("Unable to load branches/locations.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocationId("");
    loadLocations(branchId).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // simple debounce search
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const params = { limit: 50, offset: 0 };
        if (batchQuery && batchQuery.trim()) params.q = batchQuery.trim();
        const bres = await adminInventoryApi.listReceivableBatches(params);
        if (bres?.ok) {
          setBatches(Array.isArray(bres.rows) ? bres.rows : []);
          setBatchesTotal(Number(bres.total || 0));
          // if current selection is no longer in result set, clear it
          if (selectedBatchId && !(bres.rows || []).some((r) => String(r.id) === String(selectedBatchId))) {
            setSelectedBatchId("");
          }
        }
      } catch (e) {
        // non-fatal; keep existing list
      }
    }, 350);
    return () => clearTimeout(t);
  }, [batchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!branchId) return setError("Branch is required.");
    if (!locationId) return setError("Location is required.");
    if (!selectedBatchId) return setError("Batch is required.");

    setLoading(true);
    try {
      const payload = {
        batchId: Number(selectedBatchId),
        locationId: Number(locationId),
        meta: note ? { note } : undefined,
      };

      const data = await adminInventoryApi.receiveManufactured(payload);
      if (!data || data.ok !== true) throw new Error("receive_failed");

      setSuccess("Stock received successfully.");
      setNote("");
      setSelectedBatchId("");

      // refresh list after receiving (batch should disappear because it's now received)
      const params = { limit: 50, offset: 0 };
      if (batchQuery && batchQuery.trim()) params.q = batchQuery.trim();
      const bres = await adminInventoryApi.listReceivableBatches(params);
      if (bres?.ok) {
        setBatches(Array.isArray(bres.rows) ? bres.rows : []);
        setBatchesTotal(Number(bres.total || 0));
      }
    } catch (err) {
      console.error("[AdminReceiveManufacturedPage] submit error:", err);
      setError("Unable to receive manufactured stock. Check batch/location selection.");
    } finally {
      setLoading(false);
    }
  }

  function goToStockLevels() {
    if (!branchId) return;
    const bId = selectedBatchId ? String(selectedBatchId) : "";
    navigate(`/stock-levels?branchId=${encodeURIComponent(branchId)}${bId ? `&batchId=${encodeURIComponent(bId)}` : ""}`);
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Receive Manufactured Stock</h1>
            <p className="ucx-admin-subtitle">
              Explicit receiving only. Adds batch stock into a specific location.
            </p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail && <span className="ucx-small">Signed in as {adminEmail}</span>}

            <div className="ucx-admin-header-actions">
              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={goToStockLevels}
                disabled={!branchId}
              >
                View Stock Levels
              </button>
              <button
                type="button"
                className="ucx-admin-btn ucx-admin-btn-reject"
                onClick={onLogout}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        <div className="ucx-admin-panel">
          <div className="ucx-admin-panel-title">Receive</div>

          <form className="ucx-admin-form" onSubmit={submit}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Branch</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} {b.code ? `(${b.code})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Location</label>
                <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                  <option value="">Select a location…</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} {l.code ? `(${l.code})` : ""} [{l.type}]
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Search batch</label>
                <input
                  value={batchQuery}
                  onChange={(e) => setBatchQuery(e.target.value)}
                  placeholder="Search by UCX batch, product, SKU, manufacturer…"
                />
                <div className="ucx-small" style={{ marginTop: 6, opacity: 0.8 }}>
                  Showing {batches.length} of {batchesTotal}
                </div>
              </div>

              <div className="ucx-admin-field">
                <label>Batch (UCX)</label>
                <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
                  <option value="">Select a batch…</option>
                  {batches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {(b.ucx_batch || `BATCH-${b.id}`)} — {b.product_name || b.product_id} — {b.manufacturer_name || b.manufacturer_prefix || "—"} — size {b.batch_size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-admin-field">
                <label>Manufacturer</label>
                <input value={selectedBatch ? (selectedBatch.manufacturer_name || selectedBatch.manufacturer_prefix || "—") : ""} disabled />
              </div>

              <div className="ucx-admin-field">
                <label>Product</label>
                <input value={selectedBatch ? (selectedBatch.product_name || selectedBatch.product_id || "—") : ""} disabled />
              </div>

              <div className="ucx-admin-field">
                <label>Batch size (fixed)</label>
                <input value={selectedBatch ? String(selectedBatch.batch_size || "") : ""} disabled />
              </div>

              <div className="ucx-admin-field" style={{ gridColumn: "span 3" }}>
                <label>Note (optional)</label>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. First receipt from factory"
                />
              </div>
            </div>

            <div className="ucx-admin-form-actions">
              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={loading}>
                {loading ? "Receiving…" : "Receive Stock"}
              </button>

              <span className="ucx-admin-toolbar-hint">
                Branch: <b>{selectedBranch ? selectedBranch.name : "—"}</b>
              </span>
            </div>

            {!!error && <div className="ucx-admin-status-error" style={{ marginTop: 12 }}>{error}</div>}
            {!!success && <div className="ucx-admin-status-success" style={{ marginTop: 12 }}>{success}</div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default AdminReceiveManufacturedPage;
